import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNodeAssessments, ResultBadge } from '../../components/learning/NodeResults';
import AwayGuard from '../../components/security/AwayGuard';
import { useTabLabel } from '../../components/shell/TabsContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  ArrowLeft,
  CheckCircle,
  FolderSimple,
  File,
  FilePdf,
  FileDoc,
  Video,
  MusicNote,
  Link as LinkIcon,
  Exam,
  CaretRight,
  CaretLeft,
  CaretDown,
  List,
  X,
  Play,
  Lock,
  Moon,
  Sun,
  Certificate,
  Warning,
  DownloadSimple,
  NotePencil,
  SidebarSimple,
  ListChecks,
  TreeStructure,
  Info,
  ChatsCircle, Target} from '@phosphor-icons/react';
import { toast } from 'sonner';
import VideoPlayer, { formatTime } from '../../components/learning/VideoPlayer';
import LessonPanel from '../../components/learning/LessonPanel';
import RichHtml from '../../components/richtext/RichText';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';

const getNodeIcon = (nodeType, contentType) => {
  if (nodeType === 'section' || nodeType === 'subsection') return FolderSimple;
  if (nodeType === 'exam') return Exam;
  if (contentType === 'pdf') return FilePdf;
  if (contentType === 'ppt' || contentType === 'docx') return FileDoc;
  if (contentType === 'video') return Video;
  if (contentType === 'audio' || contentType === 'text_audio') return MusicNote;
  if (contentType === 'external_link') return LinkIcon;
  return File;
};


// Flatten tree for simpler rendering
const flattenTree = (nodes, depth = 0, result = []) => {
  nodes.forEach(node => {
    result.push({ ...node, depth });
    if (node.children && node.children.length > 0) {
      flattenTree(node.children, depth + 1, result);
    }
  });
  return result;
};

export default function CoursePlayerTree() {
  const { courseId } = useParams();
  // ?node=<id> opens straight onto one topic — how the diary's homework links here.
  const [searchParams] = useSearchParams();
  const requestedNodeId = searchParams.get('node');
  const { api, token, user } = useAuth();
  const navigate = useNavigate();
  const isStaff = user?.role === 'instructor' || user?.role === 'tenant_admin' || user?.role === 'super_admin';
  const isPreview = !!user?._preview; // "view as a student" window: learner UI, nothing recorded
  const [nodeExam, setNodeExam] = useState(null); // exam summary for the selected exam node
  
  const [course, setCourse] = useState(null);
  useTabLabel(course?.title ? (isStaff ? `Preview: ${course.title}` : course.title) : null);
  const [tree, setTree] = useState([]);
  const [flatNodes, setFlatNodes] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // server message when the course cannot be opened
  const [selectedNode, setSelectedNode] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('lms-course-tree') === 'closed');
  const toggleTree = () => setSidebarCollapsed((v) => { localStorage.setItem('lms-course-tree', v ? 'open' : 'closed'); return !v; });
  const [theme, setTheme] = useState(() => localStorage.getItem('lms-theme') || 'light');
  
  // Resizable sidebar state
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('lms-sidebar-width');
    return saved ? parseInt(saved, 10) : 320;
  });
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);
  const playerRef = useRef(null);     // VideoPlayer API (getCurrentTime / pause / seekTo)
  const panelRef = useRef(null);      // LessonPanel API (takeNote)
  // Beside the lesson on a wide screen, remembered between visits. On a phone the panel
  // is a sheet over the lesson, so it always starts closed — arriving at a lesson you
  // cannot see, behind your own notes, is not a way in.
  const [panelOpen, setPanelOpen] = useState(() => (
    typeof window !== 'undefined' && window.innerWidth < 1024
      ? false
      : localStorage.getItem('lms-lesson-panel') !== 'closed'
  ));
  const togglePanel = () => setPanelOpen((v) => { localStorage.setItem('lms-lesson-panel', v ? 'closed' : 'open'); return !v; });
  const [panelCounts, setPanelCounts] = useState({});           // kept so the collapsed rail can show badges
  // The teacher's marks for THIS lesson, shown as a chip beside its title. Only for a
  // learner looking at their own course — a staff preview has no diary behind it.
  const results = useNodeAssessments({
    api,
    nodeId: selectedNode?.id,
    enabled: !isPreview && user?.role === 'learner' && !!selectedNode?.id,
  });
  const [tabRequest, setTabRequest] = useState(null);           // {tab, n} — open the panel on this tab
  const openPanelAt = (tab) => { setPanelOpen(true); localStorage.setItem('lms-lesson-panel', 'open'); setTabRequest((r) => ({ tab, n: (r?.n || 0) + 1 })); };
  const collapsePanel = () => { setPanelOpen(false); localStorage.setItem('lms-lesson-panel', 'closed'); };
  const [noteRequest, setNoteRequest] = useState(0); // bumped by "Take note"; runs once the panel is mounted
  const askForNote = () => { setPanelOpen(true); localStorage.setItem('lms-lesson-panel', 'open'); setNoteRequest((n) => n + 1); };
  const [videoTime, setVideoTime] = useState(0);
  
  // Access control state
  const [accessMap, setAccessMap] = useState({});
  const [courseAvailable, setCourseAvailable] = useState(true);
  const [courseAvailabilityReason, setCourseAvailabilityReason] = useState(null);
  
  // Certificate state
  const [certificate, setCertificate] = useState(null);
  const [showCertificate, setShowCertificate] = useState(false);
  const [generatingCert, setGeneratingCert] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Theme toggle
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('lms-theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);
  
  // Resize handlers
  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const newWidth = e.clientX;
      const clampedWidth = Math.max(200, Math.min(500, newWidth));
      setSidebarWidth(clampedWidth);
      localStorage.setItem('lms-sidebar-width', clampedWidth.toString());
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);
  
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [marking, setMarking] = useState(false);
  
  const progressMap = progress.reduce((acc, p) => {
    acc[p.node_id] = p;
    return acc;
  }, {});
  
  // "Take note" works whether or not the panel is already open: the panel mounts first, then we ask it.
  useEffect(() => {
    if (!noteRequest) return undefined;
    let tries = 0;
    const tick = () => {
      if (panelRef.current?.takeNote) { panelRef.current.takeNote(); panelRef.current.scrollIntoView?.(); return; }
      if (tries++ < 20) setTimeout(tick, 50);
    };
    const t = setTimeout(tick, 0);
    return () => clearTimeout(t);
  }, [noteRequest]);

  useEffect(() => {
    if (!tabRequest) return undefined;
    let tries = 0;
    const tick = () => { if (panelRef.current?.showTab) panelRef.current.showTab(tabRequest.tab); else if (tries++ < 20) setTimeout(tick, 50); };
    const t = setTimeout(tick, 0);
    return () => clearTimeout(t);
  }, [tabRequest]);

  useEffect(() => {
    const onKey = (e) => {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      const typing = /^(INPUT|TEXTAREA)$/.test(e.target?.tagName) || e.target?.isContentEditable;
      if (typing) return;
      if (e.key === 'ArrowLeft' && prevNode) { e.preventDefault(); goToNode(prevNode); }
      if (e.key === 'ArrowRight' && nextNode) { e.preventDefault(); goToNode(nextNode); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.get(`/courses/${courseId}/tree`);
      setCourse(res.data.course);
      setTree(res.data.tree);
      setProgress(res.data.progress || []);
      
      const flat = flattenTree(res.data.tree);
      setFlatNodes(flat);
      
      // Expand everything that has children (chapters, topics and nested subtopics)
      const expandIds = new Set(flat.filter(n => flat.some(c => c.parent_id === n.id)).map(n => n.id));
      setExpandedNodes(expandIds);
      
      // Fetch access map for pace control
      try {
        const accessRes = await api.get(`/courses/${courseId}/nodes/access-map`);
        setCourseAvailable(accessRes.data.course_available);
        setCourseAvailabilityReason(accessRes.data.course_availability_reason || null);
        setAccessMap(accessRes.data.nodes || {});
      } catch (accessErr) {
        console.error('Failed to fetch access map:', accessErr);
        // Default to all accessible if fetch fails
        const defaultAccess = {};
        flat.forEach(n => { defaultAccess[n.id] = { can_access: true }; });
        setAccessMap(defaultAccess);
      }
      
      // Fetch existing certificate if any
      try {
        const certRes = await api.get('/certificates');
        const existingCert = certRes.data.find(c => c.course_id === courseId);
        if (existingCert) setCertificate(existingCert);
      } catch (certErr) {
        console.error('Failed to fetch certificates:', certErr);
      }
      
      // Open the lesson the link asked for — a homework item in the diary, a bookmark,
      // anything that points at one topic. Falling back to the first lesson when the
      // id is stale beats landing on an empty page.
      const wanted = requestedNodeId && flat.find((n) => n.id === requestedNodeId);
      const first = wanted || flat.find(n => n.node_type !== 'section' && n.node_type !== 'subsection');
      if (first) {
        setSelectedNode(first);
        // On a phone the contents drawer covers the whole screen. If we already know which
        // lesson to show, show it — don't make the student dismiss a menu to reach it.
        if (window.innerWidth < 1024) setSidebarOpen(false);
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      setLoadError(
        typeof detail === 'string' ? detail
          : status === 404 ? 'This course is no longer available. It may have been removed or you are not enrolled in it.'
            : status === 403 ? 'You do not have access to this course.'
              : 'The course could not be loaded. Check your connection and try again.'
      );
    } finally {
      setLoading(false);
    }
    // requestedNodeId is in here so that following a second homework link into the same
    // course actually moves to the new topic instead of sitting on the old one.
  }, [api, courseId, requestedNodeId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const toggleExpand = (nodeId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };
  
  const isNodeVisible = (node) => {
    if (!node.parent_id) return true;
    const parent = flatNodes.find(n => n.id === node.parent_id);
    if (!parent) return true;
    if (!expandedNodes.has(parent.id)) return false;
    return isNodeVisible(parent);
  };
  
  const handleSelectNode = (node) => {
    // Allow any node to be selected (show content or "no content" message)
    // Check if node is accessible (pace control)
    const access = accessMap[node.id];
    if (access && !access.can_access) {
      toast.error(access.reason || 'This lesson is locked. Complete previous lessons first.');
      return;
    }
    
    setSelectedNode(node);
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };
  
  const handleMarkComplete = async () => {
    if (isPreview) { toast.info('Preview only — progress is not recorded'); return; }
    if (!selectedNode) return;
    if (selectedNode.node_type === 'exam' || selectedNode.content_type === 'exam' || selectedNode.content_type === 'quiz') {
      toast.info('This lesson is completed by passing its quiz.');
      return;
    }
    setMarking(true);
    try {
      await api.post('/progress/node', {
        node_id: selectedNode.id,
        course_id: courseId,
        completed: true,
        time_spent_seconds: 0
      });
      toast.success('Progress saved!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save progress');
    } finally {
      setMarking(false);
    }
  };
  
  const calculateProgress = () => {
    const contentNodes = flatNodes.filter(n => n.node_type !== 'section' && n.node_type !== 'subsection');
    const completed = contentNodes.filter(n => progressMap[n.id]?.completed).length;
    return contentNodes.length > 0 ? (completed / contentNodes.length) * 100 : 0;
  };
  
  // Generate certificate
  const handleGenerateCertificate = async () => {
    setGeneratingCert(true);
    try {
      const res = await api.post(`/courses/${courseId}/certificate`);
      setCertificate(res.data);
      setShowCertificate(true);
      toast.success('Certificate generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cannot generate certificate yet');
    } finally {
      setGeneratingCert(false);
    }
  };
  
  // Download certificate PDF
  const handleDownloadPdf = async () => {
    if (!certificate) return;
    setDownloadingPdf(true);
    try {
      const response = await api.get(`/certificates/${certificate.id}/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificate_${certificate.certificate_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Certificate downloaded!');
    } catch (error) {
      toast.error('Failed to download certificate');
    } finally {
      setDownloadingPdf(false);
    }
  };
  
  // Check if all nodes are completed
  const isAllCompleted = () => {
    const contentNodes = flatNodes.filter(n => n.node_type !== 'section' && n.node_type !== 'subsection');
    return contentNodes.length > 0 && contentNodes.every(n => progressMap[n.id]?.completed);
  };
  
  useEffect(() => {
    let cancelled = false;
    setNodeExam(null);
    if (selectedNode?.node_type === 'exam' && selectedNode.exam_id) {
      api.get(`/exams/${selectedNode.exam_id}`)
        .then((res) => { if (!cancelled) setNodeExam(res.data); })
        .catch(() => { if (!cancelled) setNodeExam({ missing: true }); });
    }
    return () => { cancelled = true; };
  }, [api, selectedNode?.id, selectedNode?.exam_id, selectedNode?.node_type]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className={`h-full flex items-center justify-center ${theme === 'dark' ? 'bg-[#0A0A0A] text-white' : 'bg-gray-50 text-gray-900'}`}>
          <div className="animate-pulse">Loading course...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (loadError) {
    const goBack = () => { if (isPreview) { if (window.opener) window.close(); else window.history.back(); } else navigate(isStaff ? '/instructor/courses' : '/learn'); };
    return (
      <DashboardLayout>
        <div className="h-full flex items-center justify-center p-6" data-testid="course-load-error">
          <div className="max-w-md w-full rounded-lg border border-border bg-card p-6 text-center space-y-3">
            <Warning className="w-9 h-9 mx-auto text-amber-500" weight="duotone" />
            <h2 className="font-heading text-base font-semibold">Course can't be opened</h2>
            <p className="text-[13px] text-muted-foreground">{loadError}</p>
            <div className="flex justify-center gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={goBack} data-testid="course-error-back"><ArrowLeft className="w-4 h-4" /> {isPreview ? 'Close preview' : isStaff ? 'Back to courses' : 'Back to dashboard'}</Button>
              <Button size="sm" onClick={fetchData} data-testid="course-error-retry">Try again</Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  const progressPercent = calculateProgress();
  const Icon = selectedNode ? getNodeIcon(selectedNode.node_type, selectedNode.content_type) : File;
  const isCompleted = selectedNode ? progressMap[selectedNode.id]?.completed : false;
  const isLesson = !!selectedNode && (selectedNode.node_type === 'content' || selectedNode.node_type === 'file');
  // Previous / next across the whole course, in the order the tree reads
  const navIndex = selectedNode ? flatNodes.findIndex((n) => n.id === selectedNode.id) : -1;
  const prevNode = navIndex > 0 ? flatNodes[navIndex - 1] : null;
  const nextNode = navIndex > -1 && navIndex < flatNodes.length - 1 ? flatNodes[navIndex + 1] : null;
  const goToNode = (node) => {
    if (!node) return;
    handleSelectNode(node);
    // make sure the target is reachable in the tree, then bring the reader back to the top
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      let p = node.parent_id;
      while (p) { next.add(p); p = flatNodes.find((n) => n.id === p)?.parent_id; }
      return next;
    });
    const scroller = containerRef.current?.querySelector('[data-lesson-scroll]');
    (scroller || window).scrollTo?.({ top: 0, behavior: 'smooth' });
  };
  // Quiz / exam lessons are completed by passing the quiz — the server writes that progress on submit.
  const isAssessmentNode = !!selectedNode && (selectedNode.node_type === 'exam' || selectedNode.content_type === 'exam' || selectedNode.content_type === 'quiz');
  const completeLocked = isAssessmentNode && !isCompleted;
  const completeLockReason = !isAssessmentNode ? null
    : !selectedNode.exam_id ? 'No quiz is linked to this lesson yet.'
      : nodeExam?.missing ? 'The linked quiz no longer exists.'
        : `Pass this quiz to complete the lesson (${nodeExam?.settings?.passing_score ?? 60}% needed).`;
  
  return (
    <DashboardLayout>
      {/* Course reading is in scope for the tenant's leave-the-screen rule. */}
      <AwayGuard area="reading" disabled={isPreview} />
    <div className={`h-full flex flex-col ${theme === 'dark' ? 'bg-[#0A0A0A] text-white' : 'bg-gray-50 text-gray-900'}`} data-testid="course-player-tree">
      {/* Header (course bar — sits under the app's tab strip, inside the same window) */}
      <header className={`h-12 flex-shrink-0 border-b ${theme === 'dark' ? 'border-white/10 bg-black/50' : 'border-gray-200 bg-white'} z-30 flex items-center justify-between px-1 sm:px-3 gap-1`}>
        <div className="flex items-center gap-1 sm:gap-4 min-w-0 flex-1">
          <Button variant="ghost" size="icon" className={theme === 'dark' ? 'text-white hover:bg-white/10' : ''} onClick={() => { if (isPreview) { if (window.opener) window.close(); else window.history.back(); } else navigate(isStaff ? '/instructor/courses' : '/learn/my-courses'); }} title={isPreview ? 'Close preview' : isStaff ? 'Back to courses' : 'Back to My Learning'}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-medium text-sm md:text-base line-clamp-1 min-w-0">{course?.title}{isStaff && <span className="ml-2 text-[11px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 bg-amber-100 text-amber-800">Preview</span>}</h1>
        </div>
        <div className="flex items-center gap-0.5 sm:gap-4 flex-shrink-0">
          <div className="hidden md:flex items-center gap-3">
            <span className={`text-xs ${theme === 'dark' ? 'text-white/60' : 'text-gray-500'}`}>{Math.round(progressPercent)}% complete</span>
            <Progress value={progressPercent} className="w-32 h-2" />
          </div>
          {/* Mark Complete Button in Header */}
          {selectedNode && (
            <Button
              onClick={handleMarkComplete}
              disabled={marking || isCompleted || completeLocked}
              size="sm"
              className={isCompleted ? 'bg-emerald-600 hover:bg-emerald-600' : ''}
              title={completeLocked ? completeLockReason : undefined}
              data-testid="mark-complete-btn"
            >
              {isCompleted ? (
                <><CheckCircle className="w-4 h-4 mr-1" weight="fill" />Done</>
              ) : completeLocked ? (
                <><Lock className="w-4 h-4 mr-1" />Complete</>
              ) : marking ? 'Saving...' : (
                <><CheckCircle className="w-4 h-4 mr-1" />Complete</>
              )}
            </Button>
          )}
          {isLesson && (
            <Button variant="ghost" size="icon" className={`${panelOpen ? 'text-shell-primary' : ''} ${theme === 'dark' ? 'text-white hover:bg-white/10' : ''}`} onClick={() => (panelOpen ? collapsePanel() : openPanelAt('overview'))} title={panelOpen ? 'Collapse notes & community' : 'Show notes & community'} data-testid="toggle-lesson-panel">
              <SidebarSimple className="w-5 h-5" style={{ transform: 'scaleX(-1)' }} />
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={toggleTheme} title={theme === 'light' ? 'Dark mode' : 'Light mode'}>
            {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </Button>
          <Button variant="ghost" size="icon" className="hidden lg:flex" onClick={toggleTree} title={sidebarCollapsed ? 'Show course content' : 'Hide course content'} data-testid="toggle-course-tree-bar">
            <List className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="icon" className={`lg:hidden ${theme === 'dark' ? 'text-white hover:bg-white/10' : ''}`} onClick={() => setSidebarOpen(!sidebarOpen)}>
            <List className="w-5 h-5" />
          </Button>
        </div>
      </header>
      
      <div className="flex flex-1 min-h-0" ref={containerRef}>
        {/* Sidebar */}
        <aside 
          className={`fixed top-0 left-0 bottom-0 lg:static lg:h-full lg:overflow-hidden flex flex-col ${sidebarCollapsed ? 'lg:w-0 lg:overflow-hidden' : ''} ${theme === 'dark' ? 'bg-[#121212] border-r border-white/10' : 'bg-white border-r border-gray-200'} transform transition-all duration-300 z-40 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
          style={{ width: sidebarCollapsed ? 0 : `${sidebarWidth}px` }}
        >
          <div className={`p-4 border-b ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'} flex items-center gap-2`}>
            <button type="button" onClick={toggleTree} title="Hide course content" data-testid="toggle-course-tree"
              className={`hidden lg:flex w-7 h-7 rounded items-center justify-center flex-shrink-0 ${theme === 'dark' ? 'text-white/70 hover:bg-white/10' : 'text-muted-foreground hover:bg-muted'}`}>
              <TreeStructure className="w-4 h-4" />
            </button>
            <h3 className="font-heading font-semibold text-sm flex-1 min-w-0 truncate">Course Content</h3>
            <Button variant="ghost" size="icon" className={`lg:hidden ${theme === 'dark' ? 'text-white hover:bg-white/10' : ''}`} onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          <ScrollArea className="flex-1 min-h-0 h-[calc(100vh-10rem)] lg:h-auto">
            <div className="py-2">
              {flatNodes.filter(isNodeVisible).map((node) => {
                const NodeIcon = getNodeIcon(node.node_type, node.content_type);
                const hasChildren = flatNodes.some(n => n.parent_id === node.id);
                const isExpanded = expandedNodes.has(node.id);
                const isSelected = selectedNode?.id === node.id;
                const isNodeCompleted = progressMap[node.id]?.completed;
                
                // Check access control (pace control)
                const nodeAccess = accessMap[node.id] || { can_access: true };
                const isLocked = !nodeAccess.can_access;
                
                return (
                  <button
                    key={node.id}
                    className={`w-full min-w-0 flex items-center gap-2 py-2 px-3 text-left transition-colors ${
                      isLocked 
                        ? (theme === 'dark' ? 'text-white/30 cursor-not-allowed' : 'text-gray-400 cursor-not-allowed')
                        : isSelected 
                          ? (theme === 'dark' ? 'bg-white/10 text-white' : 'bg-primary/10 text-primary') 
                          : (theme === 'dark' ? 'text-white/70 hover:bg-white/5 hover:text-white' : 'text-gray-700 hover:bg-gray-100')
                    }`}
                    style={{ paddingLeft: `${node.depth * 16 + 12}px` }}
                    onClick={() => {
                      if (hasChildren && !isExpanded) toggleExpand(node.id); // opening a node reveals its children; the caret collapses
                      handleSelectNode(node);
                    }}
                    data-testid={`nav-node-${node.id}`}
                  >
                    {hasChildren ? (
                      <span role="button" tabIndex={-1} className="w-4 h-4 flex items-center justify-center rounded hover:bg-black/10" onClick={(e) => { e.stopPropagation(); toggleExpand(node.id); }} title={isExpanded ? 'Collapse' : 'Expand'}>
                        {isExpanded ? <CaretDown className="w-3 h-3" weight="bold" /> : <CaretRight className="w-3 h-3" weight="bold" />}
                      </span>
                    ) : (
                      <span className="w-4" />
                    )}
                    
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isLocked ? 'bg-gray-500/50' : isNodeCompleted ? 'bg-emerald-500' : 'bg-white/20'
                    }`}>
                      {isLocked ? <Lock className="w-3 h-3" weight="fill" /> : isNodeCompleted ? <CheckCircle className="w-3 h-3" weight="fill" /> : <NodeIcon className="w-3 h-3" />}
                    </span>
                    
                    <span className="flex-1 min-w-0 text-sm truncate" title={node.title}>{node.title}</span>
                    
                    {isLocked && (
                      <Lock className="w-3 h-3 text-gray-500" />
                    )}
                    {node.is_required && !isLocked && (
                      <span className="text-[10px] bg-amber-500/20 text-amber-400 px-1 rounded">REQ</span>
                    )}
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </aside>

        {/* Collapsed: a floating tree icon brings the course content back */}
        {sidebarCollapsed && (
          <div className={`hidden lg:flex flex-col items-center py-2 w-11 flex-shrink-0 border-r ${theme === 'dark' ? 'border-white/10 bg-black/30' : 'border-gray-200 bg-muted/30'}`} data-testid="course-tree-rail">
            <button type="button" onClick={toggleTree} title="Show course content" data-testid="show-course-tree"
              className={`w-9 h-9 rounded-md flex items-center justify-center ${theme === 'dark' ? 'text-white/70 hover:bg-white/10 hover:text-white' : 'text-muted-foreground hover:bg-background hover:text-shell-primary'}`}>
              <TreeStructure className="w-[18px] h-[18px]" />
            </button>
          </div>
        )}
        
        {/* Resize Handle */}
        {!sidebarCollapsed && (
          <div
            className={`hidden lg:block w-1 cursor-col-resize hover:bg-primary/50 active:bg-primary transition-colors flex-shrink-0 ${isResizing ? 'bg-primary' : theme === 'dark' ? 'bg-white/10' : 'bg-gray-200'}`}
            onMouseDown={handleResizeMouseDown}
            data-testid="learner-resize-handle"
          />
        )}
        
        {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}
        
        {/* Main Content */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          {/* Course Availability Banner */}
          {!courseAvailable && (
            <div className={`p-4 ${theme === 'dark' ? 'bg-red-900/30 border-red-500/30' : 'bg-red-50 border-red-200'} border-b flex items-center gap-3`}>
              <Warning className="w-5 h-5 text-red-500" weight="fill" />
              <div>
                <p className={`font-medium ${theme === 'dark' ? 'text-red-400' : 'text-red-700'}`}>Course Not Available</p>
                <p className={`text-sm ${theme === 'dark' ? 'text-red-400/70' : 'text-red-600'}`}>{courseAvailabilityReason || 'This course is currently unavailable.'}</p>
              </div>
            </div>
          )}
          
          {/* Course Completion & Certificate Banner */}
          {isAllCompleted() && courseAvailable && (
            <div className={`p-4 ${theme === 'dark' ? 'bg-emerald-900/30 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200'} border-b flex items-center justify-between`}>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-500" weight="fill" />
                <div>
                  <p className={`font-medium ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-700'}`}>
                    {certificate ? 'Course Completed!' : 'Congratulations! You completed all lessons.'}
                  </p>
                  <p className={`text-sm ${theme === 'dark' ? 'text-emerald-400/70' : 'text-emerald-600'}`}>
                    {certificate
                      ? `Certificate #${certificate.certificate_number}`
                      : course?.enable_certificate === false
                        ? 'This course does not include a certificate.'
                        : 'Claim your certificate of completion.'}
                  </p>
                </div>
              </div>
              {course?.enable_certificate !== false && (
                <Button
                  onClick={certificate ? () => setShowCertificate(true) : handleGenerateCertificate}
                  disabled={generatingCert}
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="certificate-btn"
                >
                  <Certificate className="w-4 h-4" />
                  {generatingCert ? 'Generating...' : certificate ? 'View Certificate' : 'Get Certificate'}
                </Button>
              )}
            </div>
          )}
          
          {!selectedNode ? (
            <div className={`h-full flex items-center justify-center ${theme === 'dark' ? 'text-white/40' : 'text-gray-400'}`}>
              <div className="text-center">
                <Play className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <p>Select a lesson to start learning</p>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col overflow-hidden">
              {/* Content Header - Always on top */}
              <div className="flex-shrink-0 px-4 md:px-8 pt-3 md:pt-6 pb-2 md:pb-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Icon className={`w-5 h-5 ${theme === 'dark' ? 'text-white/60' : 'text-gray-500'}`} />
                      <span className={`text-sm ${theme === 'dark' ? 'text-white/60' : 'text-gray-500'}`}>{({ section: 'Chapter', subsection: 'Topic', exam: 'Exam' })[selectedNode.node_type] || (selectedNode.content_type === 'exam' ? 'Exam' : 'Subtopic')}</span>
                      {/* step through the course */}
                      <span className="inline-flex items-center gap-1 ml-1">
                        <button type="button" onClick={() => goToNode(prevNode)} disabled={!prevNode} data-testid="prev-node"
                          title={prevNode ? `Previous: ${prevNode.title}  (Alt + ←)` : 'This is the first item'}
                          className={`w-7 h-7 rounded-md flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed ${theme === 'dark' ? 'text-white/70 hover:bg-white/10 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-shell-primary'}`}>
                          <CaretLeft className="w-4 h-4" weight="bold" />
                        </button>
                        <button type="button" onClick={() => goToNode(nextNode)} disabled={!nextNode} data-testid="next-node"
                          title={nextNode ? `Next: ${nextNode.title}  (Alt + →)` : 'This is the last item'}
                          className={`w-7 h-7 rounded-md flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed ${theme === 'dark' ? 'text-white/70 hover:bg-white/10 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-shell-primary'}`}>
                          <CaretRight className="w-4 h-4" weight="bold" />
                        </button>
                        {navIndex > -1 && <span className={`text-xs tabular-nums ${theme === 'dark' ? 'text-white/40' : 'text-gray-400'}`}>{navIndex + 1} of {flatNodes.length}</span>}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h1 className={`text-xl md:text-3xl font-heading font-bold break-words min-w-0 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{selectedNode.title}</h1>
                      <ResultBadge latest={results.latest} count={results.count} dark={theme === 'dark'}
                        onClick={() => openPanelAt('results')} />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* overflow-x-hidden is the backstop: whatever an author pastes in, the page
                  never becomes something you have to drag sideways to read. Anything that
                  truly needs the width scrolls inside its own box (see .rich-scroll). */}
              <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-y-auto overflow-x-hidden lg:overflow-hidden">
              <div className="flex-1 min-w-0 flex flex-col overflow-x-hidden lg:overflow-y-auto" data-lesson-scroll>
              {/* Video Area - Best fit container with aspect ratio */}
              {selectedNode.content_type === 'video' && (selectedNode.external_url || selectedNode.file_url) && (() => {
                const url = selectedNode.external_url || (selectedNode.file_url ? selectedNode.file_url.split('|')[0] : '');
                if (!url) return null;
                const isApiVideo = url.startsWith('/api/files/');
                // uploaded videos stream from the API with the token; anything else is a direct file or an embeddable URL
                const videoSrc = isApiVideo
                  ? `${process.env.REACT_APP_BACKEND_URL}${url}/stream?token=${encodeURIComponent(token)}`
                  : (/^https?:\/\//.test(url) ? url : `${process.env.REACT_APP_BACKEND_URL}/api/files/serve?path=${encodeURIComponent(url)}&token=${encodeURIComponent(token)}`);
                
                return (
                  <div className="flex-shrink-0 px-4 md:px-8 pb-3">
                    <div 
                      className="w-full max-w-6xl mx-auto rounded-lg overflow-hidden shadow-lg bg-black"
                      style={{ aspectRatio: '16/9', maxHeight: 'calc(100vh - 300px)' }}
                    >
                      <VideoPlayer key={selectedNode.id} ref={playerRef} url={url} src={videoSrc} title={selectedNode.title} onTimeHint={setVideoTime} />
                    </div>
                    <div className="max-w-6xl mx-auto flex items-center gap-2 pt-2">
                      <Button size="sm" variant="outline" className={`h-7 ${theme === 'dark' ? 'border-white/20 text-white hover:bg-white/10' : ''}`} onClick={askForNote} data-testid="video-take-note" title="Pause the video and write a note at this point">
                        <NotePencil className="w-3.5 h-3.5" /> Take note{videoTime > 0 && playerRef.current?.kind === 'file' ? ` at ${formatTime(videoTime)}` : ''}
                      </Button>
                      <span className={`text-[12px] ${theme === 'dark' ? 'text-white/50' : 'text-gray-500'}`}>Notes pause the video and remember where you were.</span>
                    </div>
                  </div>
                );
              })()}
              
              {/* Non-video Content */}
              <div className={`px-4 md:px-8 pb-8 min-w-0 ${selectedNode.content_type === 'video' && selectedNode.external_url && !selectedNode.content_text ? 'hidden' : ''}`}>
                {/* Audio content */}
                {['audio', 'text_audio'].includes(selectedNode.content_type) && selectedNode.file_url && (
                  <div className={`p-6 rounded-xl border ${theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                    <MusicNote className={`w-10 h-10 mx-auto mb-3 ${theme === 'dark' ? 'text-white/40' : 'text-gray-400'}`} />
                    <h3 className={`text-center font-medium mb-4 ${theme === 'dark' ? 'text-white/80' : 'text-gray-700'}`}>{selectedNode.content_type === 'text_audio' ? 'Listen' : selectedNode.title}</h3>
                    {selectedNode.file_url.split(',').filter(f => f.trim()).map((fileData, idx) => {
                      const filePath = fileData.split('|')[0];
                      const fileName = fileData.split('|')[1] || 'Audio';
                      const streamUrl = filePath.startsWith('/api/files/')
                        ? `${process.env.REACT_APP_BACKEND_URL}${filePath}/stream?token=${encodeURIComponent(token)}`
                        : /^https?:\/\//.test(filePath) ? filePath
                          : `${process.env.REACT_APP_BACKEND_URL}/api/files/serve?path=${encodeURIComponent(filePath)}&token=${encodeURIComponent(token)}`;
                      return (
                        <div key={idx} className="mb-4">
                          <p className={`text-sm mb-2 ${theme === 'dark' ? 'text-white/60' : 'text-gray-500'}`}>{fileName}</p>
                          <audio controls className="w-full" data-testid={`audio-player-${idx}`}>
                            <source src={streamUrl} />
                            Your browser does not support audio playback.
                          </audio>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {selectedNode.content_text && (
                  <RichHtml html={selectedNode.content_text} className={`lesson-text max-w-4xl ${theme === 'dark' ? 'text-white/90' : ''}`} data-testid="lesson-text" />
                )}

                {selectedNode.content_type === 'external_link' && selectedNode.external_url && !selectedNode.content_text && (
                  <div className={`p-6 rounded-xl border ${theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                    <LinkIcon className={`w-8 h-8 mb-4 ${theme === 'dark' ? 'text-white/40' : 'text-gray-400'}`} />
                    <p className={`mb-4 ${theme === 'dark' ? 'text-white/60' : 'text-gray-500'}`}>External resource:</p>
                    <a href={selectedNode.external_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-2">
                      <LinkIcon className="w-4 h-4" /> Open Link
                    </a>
                  </div>
                )}
                
                {/* PDF / Document / File content */}
                {['pdf', 'ppt', 'docx'].includes(selectedNode.content_type) && selectedNode.file_url && (
                  <div className="space-y-4">
                    {selectedNode.file_url.split(',').filter(f => f.trim()).map((fileData, idx) => {
                      const filePath = fileData.split('|')[0];
                      const fileName = fileData.split('|')[1] || filePath.split('/').pop();
                      const isApiFile = filePath.startsWith('/api/files/');
                      const fileId = isApiFile ? filePath.split('/').pop() : null;
                      const viewUrl = isApiFile
                        ? `${process.env.REACT_APP_BACKEND_URL}${filePath}/stream?token=${encodeURIComponent(token)}`
                        : `${process.env.REACT_APP_BACKEND_URL}/api/files/serve?path=${encodeURIComponent(filePath)}&token=${encodeURIComponent(token)}`;
                      const isPdf = fileName.toLowerCase().endsWith('.pdf');
                      
                      return (
                        <div key={idx} className={`rounded-xl border overflow-hidden ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
                          <div className={`flex items-center justify-between p-4 ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-50'}`}>
                            <div className="flex items-center gap-3">
                              {isPdf ? <FilePdf className="w-5 h-5 text-red-500" /> : <File className="w-5 h-5 text-blue-500" />}
                              <span className="font-medium">{fileName}</span>
                            </div>
                            <a href={viewUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm inline-flex items-center gap-1">
                              <LinkIcon className="w-4 h-4" /> Open
                            </a>
                          </div>
                          {isPdf && (
                            <div className="w-full" style={{ height: '600px' }}>
                              <iframe
                                src={viewUrl}
                                className="w-full h-full"
                                title={fileName}
                                data-testid={`pdf-viewer-${idx}`}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {selectedNode.node_type === 'exam' && (
                  <div className="p-8 bg-amber-500/10 rounded-xl border border-amber-500/20 text-center" data-testid="exam-node-card">
                    <Exam className="w-16 h-16 text-amber-500 mx-auto mb-4" weight="duotone" />
                    <h2 className="text-xl font-heading font-semibold mb-2">{selectedNode.title}</h2>
                    {!selectedNode.exam_id ? (
                      <>
                        <p className={`${theme === 'dark' ? 'text-white/60' : 'text-gray-500'} mb-6`}>No exam linked to this node.</p>
                        <Button disabled className="gap-2"><Lock className="w-4 h-4" />Exam Not Available</Button>
                      </>
                    ) : nodeExam?.missing ? (
                      <>
                        <p className={`${theme === 'dark' ? 'text-white/60' : 'text-gray-500'} mb-6`}>The linked exam no longer exists.</p>
                        <Button disabled className="gap-2"><Lock className="w-4 h-4" />Exam Not Available</Button>
                      </>
                    ) : (
                      <>
                        {nodeExam && (
                          <p className={`text-sm ${theme === 'dark' ? 'text-white/60' : 'text-gray-500'} mb-1`}>
                            {nodeExam.question_count ?? nodeExam.questions?.length ?? '—'} questions
                            {nodeExam.settings?.time_limit_minutes ? ` · ${nodeExam.settings.time_limit_minutes} min` : ' · no time limit'}
                            {` · ${nodeExam.settings?.max_attempts ?? 1} attempt${(nodeExam.settings?.max_attempts ?? 1) === 1 ? '' : 's'}`}
                            {` · pass ${nodeExam.settings?.passing_score ?? 60}%`}
                          </p>
                        )}
                        {isStaff ? (
                          <>
                            <p className={`${theme === 'dark' ? 'text-white/60' : 'text-gray-500'} mb-6`}>
                              You are viewing as {user.role.replace('_', ' ')} — open the preview to see the exam as a learner will.
                              {nodeExam && !nodeExam.is_published && <span className="block text-amber-600 font-medium mt-1">This exam is not published yet; learners cannot start it.</span>}
                            </p>
                            <Button onClick={() => navigate(`/instructor/exams/${selectedNode.exam_id}/preview`)} className="gap-2" data-testid="exam-node-preview">
                              <Play className="w-4 h-4" />Preview Exam
                            </Button>
                          </>
                        ) : nodeExam && !nodeExam.is_published ? (
                          <>
                            <p className={`${theme === 'dark' ? 'text-white/60' : 'text-gray-500'} mb-6`}>This exam is not open yet. Check back later.</p>
                            <Button disabled className="gap-2"><Lock className="w-4 h-4" />Not Yet Available</Button>
                          </>
                        ) : (() => {
                          // the learner's own standing on this quiz — it is what completes the lesson
                          const prog = progressMap[selectedNode.id];
                          const best = prog?.score;
                          const passMark = nodeExam?.settings?.passing_score ?? 60;
                          const attempted = !!prog && (prog.completed || prog.status === 'attempted' || typeof best === 'number');
                          return (
                            <>
                              {prog?.completed ? (
                                <p className="text-emerald-600 font-medium mb-6" data-testid="exam-node-status">
                                  Passed{typeof best === 'number' ? ` with ${Math.round(best)}%` : ''} — this lesson is complete.
                                </p>
                              ) : attempted ? (
                                <p className="text-amber-600 font-medium mb-6" data-testid="exam-node-status">
                                  Best score {Math.round(best || 0)}% — reach {Math.round(passMark)}% to complete this lesson.
                                </p>
                              ) : (
                                <p className={`${theme === 'dark' ? 'text-white/60' : 'text-gray-500'} mb-6`} data-testid="exam-node-status">
                                  Test your knowledge with this quiz — passing it ({Math.round(passMark)}%) completes this lesson.
                                </p>
                              )}
                              <div className="flex items-center justify-center gap-2 flex-wrap">
                                <Button onClick={() => (isPreview ? navigate(`/preview/exams/${selectedNode.exam_id}`) : navigate(`/learn/exams/${selectedNode.exam_id}`, { state: { courseId, nodeId: selectedNode.id } }))} className="gap-2" data-testid="exam-node-start">
                                  <Play className="w-4 h-4" />{attempted ? 'Try again' : 'Start Exam'}
                                </Button>
                                {attempted && !isPreview && (
                                  <Button variant="outline" className="gap-2" onClick={() => navigate(`/learn/exams/${selectedNode.exam_id}/results`, { state: { courseId } })} data-testid="exam-node-results">
                                    <ListChecks className="w-4 h-4" />View results &amp; answers
                                  </Button>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </>
                    )}
                  </div>
                )}

                {!selectedNode.content_text && !selectedNode.external_url && !selectedNode.file_url && selectedNode.node_type !== 'exam' && selectedNode.node_type !== 'section' && selectedNode.node_type !== 'subsection' && (
                  <div className={`p-8 rounded-xl text-center ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-50'}`}>
                    <File className={`w-12 h-12 mx-auto mb-4 ${theme === 'dark' ? 'text-white/20' : 'text-gray-300'}`} />
                    <p className={theme === 'dark' ? 'text-white/40' : 'text-gray-400'}>No content available yet.</p>
                  </div>
                )}
              </div>

              <div className="px-6 md:px-8 pb-8">
                {/* Chapter / topic (or a subtopic with nested subtopics): what's inside */}
                {flatNodes.some(n => n.parent_id === selectedNode.id) && (
                  <div className={`mt-6 rounded-xl border ${theme === 'dark' ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`} data-testid="node-children-index">
                    <div className={`px-4 py-2 text-xs font-semibold uppercase tracking-wide border-b ${theme === 'dark' ? 'border-white/10 text-white/50' : 'border-gray-100 text-gray-500'}`}>
                      In this {selectedNode.node_type === 'section' ? 'chapter' : selectedNode.node_type === 'subsection' ? 'topic' : 'subtopic'}
                    </div>
                    <ul className="divide-y divide-gray-100 dark:divide-white/10">
                      {flatNodes.filter(n => n.parent_id === selectedNode.id).sort((a, b) => (a.order_index || 0) - (b.order_index || 0)).map((child) => {
                        const ChildIcon = getNodeIcon(child.node_type, child.content_type);
                        const done = progressMap[child.id]?.completed;
                        const locked = accessMap[child.id] && !accessMap[child.id].can_access;
                        return (
                          <li key={child.id}>
                            <button type="button" onClick={() => handleSelectNode(child)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm ${locked ? 'opacity-50 cursor-not-allowed' : theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`} data-testid={`child-node-${child.id}`}>
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${done ? 'bg-emerald-500 text-white' : theme === 'dark' ? 'bg-white/10' : 'bg-gray-100 text-gray-600'}`}>
                                {locked ? <Lock className="w-3 h-3" /> : done ? <CheckCircle className="w-3.5 h-3.5" weight="fill" /> : <ChildIcon className="w-3.5 h-3.5" />}
                              </span>
                              <span className="flex-1 truncate">{child.title}</span>
                              {child.duration_minutes > 0 && <span className={`text-xs ${theme === 'dark' ? 'text-white/40' : 'text-gray-400'}`}>{child.duration_minutes} min</span>}
                              <CaretRight className={`w-3.5 h-3.5 ${theme === 'dark' ? 'text-white/30' : 'text-gray-300'}`} />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                
                {(prevNode || nextNode) && (
                  <nav className={`mt-8 pt-4 border-t flex items-stretch gap-2 ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'}`} data-testid="lesson-nav-footer">
                    <button type="button" onClick={() => goToNode(prevNode)} disabled={!prevNode} data-testid="prev-node-footer"
                      className={`flex-1 min-w-0 text-left rounded-lg border px-3 py-2 disabled:opacity-40 ${theme === 'dark' ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}>
                      <span className={`flex items-center gap-1 text-[11px] uppercase tracking-wide ${theme === 'dark' ? 'text-white/40' : 'text-gray-400'}`}><CaretLeft className="w-3 h-3" weight="bold" />Previous</span>
                      <span className="block truncate text-[13px] font-medium">{prevNode ? prevNode.title : '—'}</span>
                    </button>
                    <button type="button" onClick={() => goToNode(nextNode)} disabled={!nextNode} data-testid="next-node-footer"
                      className={`flex-1 min-w-0 text-right rounded-lg border px-3 py-2 disabled:opacity-40 ${theme === 'dark' ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}>
                      <span className={`flex items-center justify-end gap-1 text-[11px] uppercase tracking-wide ${theme === 'dark' ? 'text-white/40' : 'text-gray-400'}`}>Next<CaretRight className="w-3 h-3" weight="bold" /></span>
                      <span className="block truncate text-[13px] font-medium">{nextNode ? nextNode.title : '—'}</span>
                    </button>
                  </nav>
                )}
              </div>
              </div>
              {/* Lesson panel: Overview · Notes · Community (lessons only).
                  Beside the lesson on a wide screen. On a phone it used to be a 460px block
                  bolted under the lesson, so reaching your notes meant scrolling past the
                  whole chapter — now it comes up over the lesson as a sheet, the way a
                  phone app does it, and the lesson is still there behind it. */}
              {isLesson && panelOpen && (
                <>
                  <button type="button" aria-label="Close panel" onClick={collapsePanel}
                    className="lg:hidden fixed inset-0 z-40 bg-black/40" data-testid="lesson-panel-backdrop" />
                  <aside
                    className={`min-w-0 fixed inset-x-0 bottom-0 top-24 z-50 rounded-t-2xl overflow-hidden shadow-2xl
                      lg:static lg:inset-auto lg:z-auto lg:rounded-none lg:shadow-none
                      lg:basis-[400px] xl:basis-[440px] lg:flex-shrink lg:flex-grow-0 lg:h-full
                      border-t lg:border-t-0 lg:border-l flex flex-col
                      ${theme === 'dark' ? 'border-white/10 bg-[#0A0A0A]' : 'border-gray-200 bg-white'}`}
                    data-testid="lesson-panel-aside"
                  >
                    {/* the grab handle a sheet is expected to have — and a second way out */}
                    <button type="button" onClick={collapsePanel} aria-label="Close panel"
                      className="lg:hidden flex-shrink-0 flex justify-center pt-2.5 pb-1.5">
                      <span className={`h-1 w-10 rounded-full ${theme === 'dark' ? 'bg-white/25' : 'bg-gray-300'}`} />
                    </button>
                    <div className="flex-1 min-h-0">
                      <LessonPanel ref={panelRef} api={api} courseId={courseId} node={selectedNode} playerRef={playerRef} user={user} theme={theme} readOnly={isPreview}
                        onCollapse={collapsePanel} onCounts={setPanelCounts} results={results} />
                    </div>
                  </aside>
                </>
              )}
              {/* Collapsed: a rail of icons — click one to reopen on that tab. On a phone it
                  sticks to the bottom of the lesson, within thumb reach, instead of sitting
                  at the very end of the content. */}
              {isLesson && !panelOpen && (
                <aside className={`flex-shrink-0 lg:w-11 sticky bottom-0 z-20 lg:static border-t lg:border-t-0 lg:border-l flex lg:flex-col items-center justify-around lg:justify-start gap-1 py-1.5 lg:py-2 backdrop-blur ${theme === 'dark' ? 'border-white/10 bg-black/60 lg:bg-black/30' : 'border-gray-200 bg-white/90 lg:bg-muted/30'}`} data-testid="lesson-panel-rail">
                  {[
                    { key: 'overview', label: 'Overview', Icon: Info },
                    { key: 'notes', label: 'Notes', Icon: NotePencil, badge: panelCounts.notes },
                    { key: 'community', label: 'Community', Icon: ChatsCircle, badge: panelCounts.community },
                    { key: 'results', label: 'Results', Icon: Target, badge: panelCounts.results },
                  ].map(({ key, label, Icon: RailIcon, badge }) => (
                    <button key={key} type="button" onClick={() => openPanelAt(key)} title={`${label} — open the panel`} data-testid={`rail-${key}`}
                      className={`relative h-10 w-12 lg:w-9 lg:h-9 rounded-md flex flex-col lg:block items-center justify-center ${theme === 'dark' ? 'text-white/70 hover:bg-white/10 hover:text-white' : 'text-muted-foreground hover:bg-background hover:text-shell-primary'}`}>
                      <RailIcon className="w-[18px] h-[18px] mx-auto" />
                      <span className="lg:hidden text-[9px] leading-none mt-0.5">{label}</span>
                      {badge ? <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-shell-primary text-white text-[10px] leading-4 text-center">{badge}</span> : null}
                    </button>
                  ))}
                </aside>
              )}
              </div>
            </div>
          )}
        </main>
      </div>
      
      {/* Certificate Dialog */}
      <Dialog open={showCertificate} onOpenChange={setShowCertificate}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Certificate className="w-5 h-5 text-emerald-500" />
              Certificate of Completion
            </DialogTitle>
            <DialogDescription className="sr-only">
              View and download your course completion certificate
            </DialogDescription>
          </DialogHeader>
          {certificate && (
            <div className="p-8 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl border-2 border-emerald-200 dark:border-emerald-800">
              <div className="text-center space-y-4">
                <div className="w-20 h-20 mx-auto bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center">
                  <Certificate className="w-10 h-10 text-emerald-600" weight="duotone" />
                </div>
                
                <div>
                  <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium uppercase tracking-wider">Certificate of Completion</p>
                  <h2 className="text-2xl font-heading font-bold mt-2">{certificate.course_title}</h2>
                </div>
                
                <div className="py-4 border-y border-emerald-200 dark:border-emerald-800">
                  <p className="text-sm text-muted-foreground">This certifies that</p>
                  <p className="text-xl font-semibold mt-1">{certificate.user_name}</p>
                  <p className="text-sm text-muted-foreground mt-2">has successfully completed this course</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Issue Date</p>
                    <p className="font-medium">{new Date(certificate.issued_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Certificate ID</p>
                    <p className="font-mono text-xs">{certificate.certificate_number}</p>
                  </div>
                </div>
                
                {certificate.exam_score !== null && certificate.exam_score !== undefined && (
                  <div className="mt-4 p-3 bg-white dark:bg-black/20 rounded-lg">
                    <p className="text-sm text-muted-foreground">Exam Score</p>
                    <p className="text-2xl font-bold text-emerald-600">{certificate.exam_score}%</p>
                  </div>
                )}
                
                {/* Download PDF Button */}
                <div className="mt-6">
                  <Button 
                    onClick={handleDownloadPdf}
                    disabled={downloadingPdf}
                    className="gap-2 bg-emerald-600 hover:bg-emerald-700 w-full"
                    data-testid="download-certificate-btn"
                  >
                    <DownloadSimple className="w-4 h-4" />
                    {downloadingPdf ? 'Downloading...' : 'Download PDF Certificate'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </DashboardLayout>
  );
}
